import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Calendar, Clock } from 'lucide-react';

/**
 * Custom DateTime Input component with forced yyyy/mm/dd HH:mm format
 * Displays and accepts dates only in yyyy/mm/dd HH:mm format
 */
export function DateTimeInput({ value, onChange, className = '', placeholder = 'гггг/мм/дд ЧЧ:ММ', ...props }) {
  const [displayValue, setDisplayValue] = useState('');

  useEffect(() => {
    // Convert ISO datetime to yyyy/mm/dd HH:mm display format
    if (value) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          setDisplayValue(`${year}/${month}/${day} ${hours}:${minutes}`);
        }
      } catch (e) {
        setDisplayValue('');
      }
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleChange = (e) => {
    let input = e.target.value;
    
    // Remove any non-digit, non-slash, non-colon, non-space characters
    input = input.replace(/[^\d/: ]/g, '');
    
    // Auto-add formatting
    const parts = input.split(' ');
    let datePart = parts[0] || '';
    let timePart = parts[1] || '';
    
    // Format date part (yyyy/mm/dd)
    const dateParts = datePart.split('/');
    if (dateParts[0]?.length > 4) {
      dateParts[0] = dateParts[0].slice(0, 4);
    }
    if (dateParts[1]?.length > 2) {
      dateParts[1] = dateParts[1].slice(0, 2);
    }
    if (dateParts[2]?.length > 2) {
      dateParts[2] = dateParts[2].slice(0, 2);
    }
    datePart = dateParts.join('/');
    
    // Auto-add slashes in date
    if (datePart.length === 4 && !datePart.includes('/')) {
      datePart = datePart + '/';
    } else if (datePart.length === 7 && datePart.split('/').length === 2) {
      datePart = datePart + '/';
    }
    
    // Format time part (HH:mm)
    const timeParts = timePart.split(':');
    if (timeParts[0]?.length > 2) {
      timeParts[0] = timeParts[0].slice(0, 2);
    }
    if (timeParts[1]?.length > 2) {
      timeParts[1] = timeParts[1].slice(0, 2);
    }
    timePart = timeParts.join(':');
    
    // Auto-add colon in time
    if (timePart.length === 2 && !timePart.includes(':')) {
      timePart = timePart + ':';
    }
    
    // Combine
    input = datePart;
    if (timePart) {
      input += ' ' + timePart;
    }
    
    // Max length
    if (input.length > 16) {
      input = input.slice(0, 16);
    }
    
    setDisplayValue(input);
    
    // Try to parse and convert to ISO format for parent component
    if (input.length >= 16) {
      const match = input.match(/^(\d{4})\/(\d{2})\/(\d{2})\s+(\d{2}):(\d{2})$/);
      if (match) {
        const [, year, month, day, hours, minutes] = match;
        const yearNum = parseInt(year, 10);
        const monthNum = parseInt(month, 10);
        const dayNum = parseInt(day, 10);
        const hoursNum = parseInt(hours, 10);
        const minutesNum = parseInt(minutes, 10);
        
        // Validate
        if (
          yearNum >= 1900 && yearNum <= 2100 &&
          monthNum >= 1 && monthNum <= 12 &&
          dayNum >= 1 && dayNum <= 31 &&
          hoursNum >= 0 && hoursNum <= 23 &&
          minutesNum >= 0 && minutesNum <= 59
        ) {
          try {
            const date = new Date(yearNum, monthNum - 1, dayNum, hoursNum, minutesNum);
            if (!isNaN(date.getTime())) {
              // Convert to ISO format for datetime-local input (yyyy-MM-ddTHH:mm)
              const isoDate = `${year}-${month}-${day}T${hours}:${minutes}`;
              onChange({ target: { value: isoDate } });
              return;
            }
          } catch (e) {
            // Invalid date
          }
        }
      }
    }
    
    // If not valid complete datetime, still update display but send empty to parent
    if (input.length < 16) {
      onChange({ target: { value: '' } });
    }
  };

  const handleBlur = () => {
    // Validate on blur
    if (displayValue && displayValue.length >= 16) {
      const match = displayValue.match(/^(\d{4})\/(\d{2})\/(\d{2})\s+(\d{2}):(\d{2})$/);
      if (!match) {
        setDisplayValue('');
        onChange({ target: { value: '' } });
      }
    } else if (displayValue && displayValue.length > 0 && displayValue.length < 16) {
      // Incomplete datetime - clear it
      setDisplayValue('');
      onChange({ target: { value: '' } });
    }
  };

  const handleNativeDateTimeChange = (e) => {
    // When native calendar is used
    const isoDateTime = e.target.value; // yyyy-MM-ddTHH:mm
    if (isoDateTime) {
      onChange({ target: { value: isoDateTime } });
    }
  };

  return (
    <div className="relative">
      <Input
        type="text"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={`pr-20 ${className}`}
        {...props}
      />
      {/* Hidden native datetime-local input for calendar picker - covering entire right side */}
      <input
        type="datetime-local"
        value={value || ''}
        onChange={handleNativeDateTimeChange}
        className="absolute right-0 top-0 w-20 h-full opacity-0 cursor-pointer z-10"
        title="Открыть календарь"
        style={{ pointerEvents: 'auto' }}
      />
      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 text-gray-400 pointer-events-none z-0">
        <Calendar className="w-4 h-4" />
        <Clock className="w-4 h-4" />
      </div>
    </div>
  );
}
